import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  FaChurch, FaUsers, FaCalendarAlt, FaPrayingHands, 
  FaBell, FaSignOutAlt, FaEdit, FaVideo,
  FaHome, FaClock, FaMapMarkerAlt, FaEnvelope,
  FaQuoteRight, FaClipboardList, FaCog, FaArrowRight,
  FaPlus, FaChartLine, FaExternalLinkAlt, FaSave,
  FaUpload, FaSpinner, FaInfoCircle, FaTimes, FaCheck,
  FaCampground
} from 'react-icons/fa';
import { API_URL } from '../../config';
import { YouTubeService } from '../../services/youtube.service';
import { PodcastService } from '../../services/podcast.service';
import testimonialsService from '../../services/testimonials.service';
import BranchForm from '../../admin/components/BranchForm';
import BranchList from '../../admin/components/BranchList';
import TestimonialForm from '../../admin/components/TestimonialForm';
import TestimonialList from '../../admin/components/TestimonialList';
import ServicesPage from './ServicesPage';
import {
  PageContainer,
  Sidebar,
  SidebarHeader,
  Logo,
  NavSection,
  NavLabel,
  NavItem,
  LogoutButton,
  MainContent,
  TopBar,
  PageTitle,
  TopBarActions,
  IconButton,
  NotificationDot,
  UserAvatar,
  ContentArea,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  StatsGrid,
  StatCard,
  StatIcon,
  StatContent,
  StatValue,
  StatLabel,
  Grid,
  Section,
  SectionHeader,
  SectionTitle,
  SectionLink,
  QuickActionCard,
  QuickActionIcon,
  QuickActionLabel,
  Alert,
  Button,
  ButtonGroup,
  FormGroup,
  Label,
  Input,
  TextArea,
  Badge,
  ActivityItem,
  ActivityIcon,
  ActivityContent,
  ActivityTitle,
  ActivityDescription,
  ActivityTime,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  StatusDot
} from '../../styles/AdminStyles';
import styled from 'styled-components';

// Navigation items configuration
const navItems = [
  { id: 'overview', label: 'Overview', icon: FaChartLine, type: 'tab' },
  { id: 'homepage', label: 'Homepage', icon: FaHome, type: 'tab' },
  { id: 'branches', label: 'Branches', icon: FaMapMarkerAlt, type: 'tab' },
  { id: 'services', label: 'Services', icon: FaClock, type: 'tab' },
  { id: 'testimonials', label: 'Testimonials', icon: FaQuoteRight, type: 'tab' },
];

const routeItems = [
  { path: '/admin/events', label: 'Events', icon: FaCalendarAlt },
  { path: '/admin/messages', label: 'Messages', icon: FaEnvelope },
  { path: '/admin/camp-registration', label: 'Camp Registration', icon: FaCampground },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useState(JSON.parse(localStorage.getItem('user')) || {});
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Data states
  const [homeContent, setHomeContent] = useState({});
  const [branches, setBranches] = useState([]);
  const [events, setEvents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [editingBranch, setEditingBranch] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [youtubeStatus, setYoutubeStatus] = useState(null);
  const [podcastStatus, setPodcastStatus] = useState(null);
  const [youtubeSyncLoading, setYoutubeSyncLoading] = useState(false);
  const [messages, setMessages] = useState([]);

  // Clear alerts after delay
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch initial data
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [contentRes, eventsRes] = await Promise.all([
        fetch(`${API_URL}/home-content/latest`),
        fetch(`${API_URL}/events`)
      ]);

      if (contentRes.ok) {
        const contentData = await contentRes.json();
        setHomeContent(contentData);
        if (contentData.branches) setBranches(contentData.branches);
      }

      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setEvents(eventsData);
      }

      // Fetch additional data
      fetchYouTubeStatus();
      fetchPodcastStatus();
      fetchTestimonials();
      fetchMessages();
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchYouTubeStatus = async () => {
    try {
      const status = await YouTubeService.getSyncStatus();
      setYoutubeStatus(status);
    } catch (error) {
      console.error('Error fetching YouTube status:', error);
    }
  };

  const fetchPodcastStatus = async () => {
    try {
      const status = await PodcastService.getServiceStatus();
      setPodcastStatus(status);
    } catch (error) {
      console.error('Error fetching podcast status:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const data = await testimonialsService.getAdminTestimonials();
      setTestimonials(data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${API_URL}/messages`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data || []);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await fetch(`${API_URL}/home-content/latest`);
      if (response.ok) {
        const data = await response.json();
        if (data.branches) setBranches(data.branches);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin');
  };

  const triggerYouTubeSync = async () => {
    setYoutubeSyncLoading(true);
    try {
      const token = localStorage.getItem('token');
      await YouTubeService.triggerManualSync(token);
      setSuccess('YouTube sync triggered successfully!');
      setTimeout(fetchYouTubeStatus, 2000);
    } catch (error) {
      setError('Failed to trigger YouTube sync: ' + error.message);
    } finally {
      setYoutubeSyncLoading(false);
    }
  };

  const handleContentUpdate = async (section, content) => {
    setIsLoading(true);
    setError(null);
    try {
      const bodyContent = section === 'services' ? content : { [section]: content };
      const response = await fetch(`${API_URL}/home-content/section/${section}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(bodyContent)
      });

      if (!response.ok) throw new Error('Failed to update content');
      
      const refreshRes = await fetch(`${API_URL}/home-content/latest`);
      if (refreshRes.ok) {
        const refreshedData = await refreshRes.json();
        setHomeContent(refreshedData);
      }
      setSuccess('Content updated successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (section, file) => {
    if (!file) return;
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/home-content/upload/${section}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });

      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      setHomeContent(prev => ({ ...prev, [section]: data.url }));
      setSuccess('Image uploaded successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBranchSubmit = async () => {
    await fetchBranches();
    setEditingBranch(null);
    setSuccess('Branch saved successfully!');
  };

  const handleTestimonialSubmit = async (data) => {
    try {
      if (editingTestimonial) {
        await testimonialsService.updateTestimonial(editingTestimonial._id, data);
      } else {
        await testimonialsService.createTestimonial(data);
      }
      await fetchTestimonials();
      setEditingTestimonial(null);
      setSuccess('Testimonial saved successfully!');
    } catch (error) {
      setError(`Failed to save testimonial: ${error.message}`);
    }
  };

  // Stats calculation
  const stats = [
    { 
      icon: FaMapMarkerAlt, 
      label: 'Branches', 
      value: branches.length,
      color: '#6366f1'
    },
    { 
      icon: FaCalendarAlt, 
      label: 'Upcoming Events', 
      value: events.filter(e => new Date(e.start?.dateTime) > new Date()).length,
      color: '#10b981'
    },
    { 
      icon: FaQuoteRight, 
      label: 'Testimonials', 
      value: testimonials.length,
      color: '#f59e0b'
    },
    { 
      icon: FaEnvelope, 
      label: 'Messages', 
      value: messages.length,
      color: '#ef4444'
    }
  ];

  // Quick actions
  const quickActions = [
    { icon: FaCalendarAlt, label: 'Add Event', onClick: () => navigate('/admin/events'), color: '#10b981' },
    { icon: FaMapMarkerAlt, label: 'Add Branch', onClick: () => setActiveTab('branches'), color: '#6366f1' },
    { icon: FaQuoteRight, label: 'Add Testimonial', onClick: () => setActiveTab('testimonials'), color: '#f59e0b' },
    { icon: FaEdit, label: 'Edit Homepage', onClick: () => setActiveTab('homepage'), color: '#8b5cf6' },
  ];

  // Recent activity
  const recentActivity = [
    ...events.slice(0, 3).map(e => ({
      type: 'event',
      title: e.summary,
      description: `Event scheduled for ${new Date(e.start?.dateTime).toLocaleDateString()}`,
      time: 'Event',
      icon: FaCalendarAlt,
      color: '#dcfce7',
      textColor: '#166534'
    })),
    ...testimonials.slice(0, 2).map(t => ({
      type: 'testimonial',
      title: `Testimonial from ${t.name}`,
      description: t.testimony?.substring(0, 50) + '...',
      time: 'Testimonial',
      icon: FaQuoteRight,
      color: '#fef3c7',
      textColor: '#92400e'
    }))
  ];

  return (
    <PageContainer>
      <Sidebar>
        <SidebarHeader>
          <Logo>
            <FaChurch />
            <span>ELC Admin</span>
          </Logo>
        </SidebarHeader>

        <NavSection>
          <NavLabel>Dashboard</NavLabel>
          {navItems.map(item => (
            <NavItem
              key={item.id}
              $active={activeTab === item.id}
              onClick={() => setActiveTab(item.id)}
            >
              <item.icon /> {item.label}
            </NavItem>
          ))}

          <NavLabel>Management</NavLabel>
          {routeItems.map(item => (
            <NavItem
              key={item.path}
              as={Link}
              to={item.path}
              $active={location.pathname === item.path}
            >
              <item.icon /> {item.label}
            </NavItem>
          ))}
        </NavSection>

        <LogoutButton onClick={handleLogout}>
          <FaSignOutAlt /> Logout
        </LogoutButton>
      </Sidebar>

      <MainContent>
        <TopBar>
          <PageTitle>
            <h1>
              {activeTab === 'overview' && 'Dashboard Overview'}
              {activeTab === 'homepage' && 'Homepage Content'}
              {activeTab === 'branches' && 'Branch Management'}
              {activeTab === 'services' && 'Service Management'}
              {activeTab === 'testimonials' && 'Testimonials'}
            </h1>
            <p>Manage your church website content</p>
          </PageTitle>
          <TopBarActions>
            <IconButton>
              <FaBell />
              {messages.length > 0 && <NotificationDot />}
            </IconButton>
            <UserAvatar>
              {user.firstName?.[0] || 'A'}
            </UserAvatar>
          </TopBarActions>
        </TopBar>

        <ContentArea>
          {error && (
            <Alert $type="error">
              <FaTimes /> {error}
            </Alert>
          )}
          
          {success && (
            <Alert $type="success">
              <FaCheck /> {success}
            </Alert>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              <StatsGrid>
                {stats.map((stat, i) => (
                  <StatCard
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <StatIcon $color={stat.color}>
                      <stat.icon />
                    </StatIcon>
                    <StatContent>
                      <StatValue>{stat.value}</StatValue>
                      <StatLabel>{stat.label}</StatLabel>
                    </StatContent>
                  </StatCard>
                ))}
              </StatsGrid>

              <Grid $cols={2} style={{ marginBottom: '2rem' }}>
                <Card>
                  <CardHeader>
                    <CardTitle><FaPlus /> Quick Actions</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <QuickActionsGrid>
                      {quickActions.map((action, i) => (
                        <QuickActionCard
                          key={i}
                          onClick={action.onClick}
                          $color={action.color}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <QuickActionIcon $color={action.color}>
                            <action.icon />
                          </QuickActionIcon>
                          <QuickActionLabel>{action.label}</QuickActionLabel>
                        </QuickActionCard>
                      ))}
                    </QuickActionsGrid>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle><FaChartLine /> Recent Activity</CardTitle>
                    <SectionLink onClick={() => navigate('/admin/events')}>
                      View all <FaArrowRight />
                    </SectionLink>
                  </CardHeader>
                  <CardBody>
                    {recentActivity.length > 0 ? (
                      recentActivity.slice(0, 5).map((activity, i) => (
                        <ActivityItem key={i}>
                          <ActivityIcon $color={activity.color} $textColor={activity.textColor}>
                            <activity.icon />
                          </ActivityIcon>
                          <ActivityContent>
                            <ActivityTitle>{activity.title}</ActivityTitle>
                            <ActivityDescription>{activity.description}</ActivityDescription>
                            <ActivityTime>
                              <Badge>{activity.time}</Badge>
                            </ActivityTime>
                          </ActivityContent>
                        </ActivityItem>
                      ))
                    ) : (
                      <EmptyState>
                        <EmptyStateIcon>📊</EmptyStateIcon>
                        <EmptyStateTitle>No recent activity</EmptyStateTitle>
                        <EmptyStateText>Start adding events and content to see activity here.</EmptyStateText>
                      </EmptyState>
                    )}
                  </CardBody>
                </Card>
              </Grid>

              <Grid $cols={2}>
                <Card>
                  <CardHeader>
                    <CardTitle><FaVideo /> YouTube Automation</CardTitle>
                    <Badge $variant={youtubeStatus?.configured ? 'success' : 'warning'}>
                      {youtubeStatus?.configured ? 'Active' : 'Not Configured'}
                    </Badge>
                  </CardHeader>
                  <CardBody>
                    <StatusGrid>
                      <StatusRow>
                        <StatusLabel>Last Sync</StatusLabel>
                        <StatusValue>{youtubeStatus?.lastSync ? new Date(youtubeStatus.lastSync).toLocaleString() : 'Never'}</StatusValue>
                      </StatusRow>
                      <StatusRow>
                        <StatusLabel>Next Sync</StatusLabel>
                        <StatusValue>{youtubeStatus?.nextSync ? new Date(youtubeStatus.nextSync).toLocaleString() : 'Unknown'}</StatusValue>
                      </StatusRow>
                      {youtubeStatus?.currentVideo && (
                        <StatusRow>
                          <StatusLabel>Current Video</StatusLabel>
                          <StatusValue>{youtubeStatus.currentVideo.title}</StatusValue>
                        </StatusRow>
                      )}
                    </StatusGrid>
                    <Button 
                      onClick={triggerYouTubeSync}
                      disabled={youtubeSyncLoading || !youtubeStatus?.configured}
                      style={{ marginTop: '1rem' }}
                    >
                      {youtubeSyncLoading ? <><FaSpinner className="spin" /> Syncing...</> : <><FaVideo /> Manual Sync</>}
                    </Button>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle><FaPrayingHands /> Podcast Status</CardTitle>
                    <Badge $variant={podcastStatus?.configured ? 'success' : 'warning'}>
                      {podcastStatus?.configured ? 'Active' : 'Not Configured'}
                    </Badge>
                  </CardHeader>
                  <CardBody>
                    <StatusGrid>
                      <StatusRow>
                        <StatusLabel>Status</StatusLabel>
                        <StatusValue>{podcastStatus?.message || 'Service unavailable'}</StatusValue>
                      </StatusRow>
                      <StatusRow>
                        <StatusLabel>Platforms</StatusLabel>
                        <StatusValue>Apple Podcasts, Podcast Addict, Podbean</StatusValue>
                      </StatusRow>
                    </StatusGrid>
                    <InfoText>
                      <FaInfoCircle /> Podcast episodes are automatically fetched from multiple platforms.
                    </InfoText>
                  </CardBody>
                </Card>
              </Grid>
            </>
          )}

          {/* Homepage Content Tab */}
          {activeTab === 'homepage' && (
            <HomepageContent
              homeContent={homeContent}
              setHomeContent={setHomeContent}
              handleContentUpdate={handleContentUpdate}
              handleImageUpload={handleImageUpload}
              isLoading={isLoading}
            />
          )}

          {/* Branches Tab */}
          {activeTab === 'branches' && (
            <Card>
              <CardHeader>
                <CardTitle><FaMapMarkerAlt /> Manage Branches</CardTitle>
              </CardHeader>
              <CardBody>
                <BranchForm 
                  onSubmit={handleBranchSubmit}
                  initialData={editingBranch}
                />
                <BranchList 
                  branches={branches}
                  onEdit={(branch) => setEditingBranch(branch)}
                  onRefresh={fetchBranches}
                />
              </CardBody>
            </Card>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <ServicesPage />
          )}

          {/* Testimonials Tab */}
          {activeTab === 'testimonials' && (
            <Card>
              <CardHeader>
                <CardTitle><FaQuoteRight /> Manage Testimonials</CardTitle>
              </CardHeader>
              <CardBody>
                <TestimonialForm 
                  onSubmit={handleTestimonialSubmit}
                  initialData={editingTestimonial}
                  onCancel={editingTestimonial ? () => setEditingTestimonial(null) : undefined}
                />
                <TestimonialList 
                  testimonials={testimonials}
                  onEdit={(testimonial) => setEditingTestimonial(testimonial)}
                  onRefresh={fetchTestimonials}
                />
              </CardBody>
            </Card>
          )}
        </ContentArea>
      </MainContent>
    </PageContainer>
  );
};

// Homepage Content Component
const HomepageContent = ({ homeContent, setHomeContent, handleContentUpdate, handleImageUpload, isLoading }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadSection, setUploadSection] = useState('');

  return (
    <Grid $cols={1}>
      <Card>
        <CardHeader>
          <CardTitle><FaEdit /> Welcome Message</CardTitle>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label>Welcome Message</Label>
            <TextArea 
              value={homeContent.welcomeMessage || ''}
              onChange={(e) => setHomeContent(prev => ({ ...prev, welcomeMessage: e.target.value }))}
              placeholder="Enter your welcome message..."
              rows={4}
            />
          </FormGroup>
          <Button 
            onClick={() => handleContentUpdate('welcomeMessage', homeContent.welcomeMessage)}
            disabled={isLoading}
          >
            <FaSave /> {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle><FaEdit /> Scripture</CardTitle>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label>Scripture Text</Label>
            <TextArea 
              value={homeContent.scriptureText || ''}
              onChange={(e) => setHomeContent(prev => ({ ...prev, scriptureText: e.target.value }))}
              placeholder="Enter scripture text..."
              rows={3}
            />
          </FormGroup>
          <FormGroup>
            <Label>Scripture Reference</Label>
            <Input
              value={homeContent.scriptureReference || ''}
              onChange={(e) => setHomeContent(prev => ({ ...prev, scriptureReference: e.target.value }))}
              placeholder="e.g., John 3:16"
            />
          </FormGroup>
          <ButtonGroup>
            <Button 
              onClick={() => handleContentUpdate('scriptureText', homeContent.scriptureText)}
              disabled={isLoading}
            >
              <FaSave /> Save Scripture
            </Button>
            <Button 
              $variant="secondary"
              onClick={() => handleContentUpdate('scriptureReference', homeContent.scriptureReference)}
              disabled={isLoading}
            >
              <FaSave /> Save Reference
            </Button>
          </ButtonGroup>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle><FaEdit /> Mission Statement</CardTitle>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label>Mission Text</Label>
            <TextArea 
              value={homeContent.missionText || ''}
              onChange={(e) => setHomeContent(prev => ({ ...prev, missionText: e.target.value }))}
              placeholder="Enter your mission statement..."
              rows={4}
            />
          </FormGroup>
          <Button 
            onClick={() => handleContentUpdate('missionText', homeContent.missionText)}
            disabled={isLoading}
          >
            <FaSave /> {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle><FaEnvelope /> Contact Form Email</CardTitle>
        </CardHeader>
        <CardBody>
          {homeContent.contactEmail && (
            <CurrentValueBox>
              <strong>Current Email:</strong> {homeContent.contactEmail}
            </CurrentValueBox>
          )}
          <FormGroup>
            <Label>Contact Email Address</Label>
            <Input
              type="email"
              value={homeContent.contactEmail || ''}
              onChange={(e) => setHomeContent(prev => ({ ...prev, contactEmail: e.target.value }))}
              placeholder="Enter email address..."
            />
          </FormGroup>
          <Button 
            onClick={() => handleContentUpdate('contactEmail', homeContent.contactEmail)}
            disabled={isLoading}
          >
            <FaSave /> {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle><FaVideo /> Latest Message</CardTitle>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label>Message Title</Label>
            <Input
              value={homeContent.latestMessage?.title || ''}
              onChange={(e) => setHomeContent(prev => ({
                ...prev,
                latestMessage: { ...prev.latestMessage, title: e.target.value }
              }))}
              placeholder="Message title..."
            />
          </FormGroup>
          <FormGroup>
            <Label>YouTube Video ID or URL</Label>
            <Input
              value={homeContent.latestMessage?.videoId || ''}
              onChange={(e) => setHomeContent(prev => ({
                ...prev,
                latestMessage: { ...prev.latestMessage, videoId: e.target.value }
              }))}
              placeholder="Enter YouTube video ID or URL..."
            />
          </FormGroup>
          <FormGroup>
            <Label>Date</Label>
            <Input
              type="date"
              value={homeContent.latestMessage?.date || ''}
              onChange={(e) => setHomeContent(prev => ({
                ...prev,
                latestMessage: { ...prev.latestMessage, date: e.target.value }
              }))}
            />
          </FormGroup>
          <Button 
            onClick={() => {
              let videoId = homeContent.latestMessage?.videoId || '';
              if (videoId.includes('youtube.com/watch?v=')) {
                videoId = videoId.split('v=')[1].split('&')[0];
              } else if (videoId.includes('youtu.be/')) {
                videoId = videoId.split('youtu.be/')[1];
              }
              handleContentUpdate('latestMessage', {
                title: homeContent.latestMessage?.title || '',
                date: homeContent.latestMessage?.date || '',
                videoId: videoId
              });
            }}
            disabled={isLoading}
          >
            <FaSave /> {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle><FaUpload /> Image Management</CardTitle>
        </CardHeader>
        <CardBody>
          <ImageGrid>
            <ImageUploadCard>
              <ImagePreview>
                {homeContent.missionImage ? (
                  <img src={homeContent.missionImage} alt="Mission" />
                ) : (
                  <FaUpload />
                )}
              </ImagePreview>
              <ImageLabel>Mission Image</ImageLabel>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setSelectedImage(file);
                    setUploadSection('missionImage');
                  }
                }}
                style={{ display: 'none' }}
                id="mission-image-upload"
              />
              <label htmlFor="mission-image-upload">
                <Button as="span" $variant="secondary" $size="sm">
                  <FaUpload /> Choose Image
                </Button>
              </label>
            </ImageUploadCard>

            <ImageUploadCard>
              <ImagePreview>
                {homeContent.pastorImage ? (
                  <img src={homeContent.pastorImage} alt="Pastor" />
                ) : (
                  <FaUpload />
                )}
              </ImagePreview>
              <ImageLabel>Pastor Image</ImageLabel>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setSelectedImage(file);
                    setUploadSection('pastorImage');
                  }
                }}
                style={{ display: 'none' }}
                id="pastor-image-upload"
              />
              <label htmlFor="pastor-image-upload">
                <Button as="span" $variant="secondary" $size="sm">
                  <FaUpload /> Choose Image
                </Button>
              </label>
            </ImageUploadCard>
          </ImageGrid>

          {selectedImage && uploadSection && (
            <UploadActions>
              <SelectedFileName>
                Selected: {selectedImage.name}
              </SelectedFileName>
              <ButtonGroup>
                <Button 
                  onClick={() => {
                    handleImageUpload(uploadSection, selectedImage);
                    setSelectedImage(null);
                    setUploadSection('');
                  }}
                  disabled={isLoading}
                >
                  <FaUpload /> {isLoading ? 'Uploading...' : 'Upload Image'}
                </Button>
                <Button 
                  $variant="secondary"
                  onClick={() => {
                    setSelectedImage(null);
                    setUploadSection('');
                  }}
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </UploadActions>
          )}
        </CardBody>
      </Card>
    </Grid>
  );
};

// Additional styled components
const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatusGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
`;

const StatusLabel = styled.span`
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
`;

const StatusValue = styled.span`
  font-size: 0.875rem;
  color: #0f172a;
  font-weight: 500;
`;

const InfoText = styled.p`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.85rem;
  margin-top: 1rem;
  font-style: italic;

  svg {
    color: #6366f1;
  }
`;

const CurrentValueBox = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #1e40af;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
`;

const ImageUploadCard = styled.div`
  text-align: center;
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 150px;
  background: #f1f5f9;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  overflow: hidden;
  border: 2px dashed #e2e8f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    font-size: 2rem;
    color: #94a3b8;
  }
`;

const ImageLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
`;

const UploadActions = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #f1f5f9;
`;

const SelectedFileName = styled.div`
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 1rem;
`;

export default Dashboard;
